import { Component, OnInit, inject } from '@angular/core';

import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { Project, Status, Visibility } from '../interfaces/project';
import { PortfolioProjectService } from '../portfolio-project.service';
import { PROJECT_CATEGORIES, ProjectCategoryId } from '../constants/project-categories';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatSelectModule } from '@angular/material/select';
import { ImagebucketPickerComponent } from './imagebucket-picker/imagebucket-picker.component';

@Component({
    selector: 'app-admin-project-form',
    imports: [
    ReactiveFormsModule,
    RouterLink,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatSelectModule,
    ImagebucketPickerComponent
],
    templateUrl: './admin-project-form.component.html',
    styleUrl: './admin-project-form.component.sass'
})
export class AdminProjectFormComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly projectService = inject(PortfolioProjectService);

  projectId: string | null = null;
  isEditMode = false;

  /** Same order as lifecycle: Planned → Active → On hold → Finished */
  readonly statusOptions: Status[] = [
    Status.planned,
    Status.active,
    Status.on_hold,
    Status.archived
  ];
  readonly visibilityOptions = Object.values(Visibility);
  readonly categoryOptions = [...PROJECT_CATEGORIES];
  existingTagOptions: string[] = [];

  projectForm = this.fb.group({
    name: ['', Validators.required],
    status: [Status.active, Validators.required],
    visibility: [Visibility.public, Validators.required],
    short_description: ['', Validators.required],
    long_description: [''],
    imageUrl: [''],
    imageUrlsText: [''],
    startDate: [''],
    endDate: [''],
    categories: [[] as string[]],
    tagsFromExisting: [[] as string[]],
    tagsExtra: ['']
  });

  async ngOnInit(): Promise<void> {
    const allProjects = await firstValueFrom(this.projectService.getProjects());
    this.existingTagOptions = this.collectDistinctTags(allProjects);

    this.projectId = this.route.snapshot.paramMap.get('id');
    this.isEditMode = !!this.projectId;

    if (!this.projectId) {
      return;
    }

    const project = await firstValueFrom(this.projectService.getProjectById(this.projectId));
    if (!project) {
      await this.router.navigate(['/admin/projects']);
      return;
    }

    const tags = project.tags ?? [];
    const tagsFromExisting = tags.filter((t) => this.existingTagOptions.includes(t));
    const tagsExtra = tags.filter((t) => !this.existingTagOptions.includes(t)).join(', ');
    const categories = (project.categories ?? []).filter((c) =>
      (PROJECT_CATEGORIES as readonly string[]).includes(c)
    );

    this.projectForm.patchValue({
      name: project.name,
      status: project.status,
      visibility: project.visibility ?? Visibility.public,
      short_description: project.short_description,
      long_description: project.long_description ?? '',
      imageUrl: project.imageUrl ?? '',
      imageUrlsText: (project.imageUrls ?? []).join('\n'),
      startDate: project.startDate ? this.toDateInput(project.startDate) : '',
      endDate: project.endDate ? this.toDateInput(project.endDate) : '',
      categories,
      tagsFromExisting,
      tagsExtra
    });
  }

  private collectDistinctTags(projects: Project[]): string[] {
    const seen = new Set<string>();
    for (const p of projects) {
      p.tags?.forEach((t) => seen.add(t));
    }
    return Array.from(seen).sort((a, b) => a.localeCompare(b));
  }

  visibilityLabel(v: Visibility): string {
    switch (v) {
      case Visibility.public:
        return 'Public';
      case Visibility.hidden:
        return 'Hidden (viewer login required)';
      case Visibility.personal:
        return 'Personal (admin only)';
      default:
        return v;
    }
  }

  async submit(): Promise<void> {
    if (this.projectForm.invalid) {
      this.projectForm.markAllAsTouched();
      return;
    }

    const value = this.projectForm.getRawValue();
    const startDate = this.parseOptionalDateField('startDate', value.startDate);
    const endDate = this.parseOptionalDateField('endDate', value.endDate);
    if (startDate === null || endDate === null) {
      return;
    }

    const imageUrls = this.parseImageUrls(value.imageUrlsText ?? '');
    const rawCategories = value.categories ?? [];
    const categories = rawCategories.filter((c): c is ProjectCategoryId =>
      (PROJECT_CATEGORIES as readonly string[]).includes(c)
    );
    const tags = this.mergeTags(value.tagsFromExisting ?? [], value.tagsExtra ?? '');

    const payload = {
      name: value.name ?? '',
      status: (value.status ?? Status.active) as Status,
      visibility: (value.visibility ?? Visibility.public) as Visibility,
      short_description: value.short_description ?? '',
      long_description: value.long_description ?? undefined,
      imageUrl: value.imageUrl ?? undefined,
      imageUrls: imageUrls.length ? imageUrls : undefined,
      startDate,
      endDate,
      categories: categories.length ? categories : undefined,
      tags
    };

    if (this.projectId) {
      await this.projectService.updateProject(this.projectId, payload);
    } else {
      await this.projectService.createProject(payload);
    }

    await this.router.navigate(['/admin/projects']);
  }

  private mergeTags(fromExisting: string[], extraRaw: string): string[] {
    const fromComma = extraRaw
      .split(',')
      .map((tag) => tag.trim())
      .filter(Boolean);
    const seen = new Set<string>();
    const out: string[] = [];
    for (const t of [...fromExisting, ...fromComma]) {
      if (seen.has(t)) {
        continue;
      }
      seen.add(t);
      out.push(t);
    }
    return out;
  }

  private parseImageUrls(value: string): string[] {
    return value
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean);
  }

  private parseOptionalDateField(
    controlName: 'startDate' | 'endDate',
    raw: string | null | undefined
  ): Date | undefined | null {
    const control = this.projectForm.get(controlName);
    const s = raw?.trim() ?? '';
    if (!s) {
      control?.setErrors(null);
      return undefined;
    }
    const parsed = this.parseFlexibleDate(s);
    if (!parsed) {
      control?.setErrors({ dateParse: true });
      control?.markAsTouched();
      return null;
    }
    control?.setErrors(null);
    return parsed;
  }

  /** Empty → undefined; YYYY-MM-DD (local) or Date.parse-compatible string → Date; invalid → undefined for parse result check */
  private parseFlexibleDate(s: string): Date | undefined {
    const iso = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(s);
    if (iso) {
      const y = Number(iso[1]);
      const m = Number(iso[2]) - 1;
      const d = Number(iso[3]);
      const dt = new Date(y, m, d);
      if (dt.getFullYear() === y && dt.getMonth() === m && dt.getDate() === d) {
        return dt;
      }
      return undefined;
    }
    const t = Date.parse(s);
    if (Number.isNaN(t)) {
      return undefined;
    }
    const dt = new Date(t);
    return Number.isNaN(dt.getTime()) ? undefined : dt;
  }

  private toDateInput(date: Date): string {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  imagebucketGalleryLines(): string[] {
    const raw = this.projectForm.get('imageUrlsText')?.value ?? '';
    return raw
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean);
  }

  onImagebucketPick(event: { mode: 'cover' | 'gallery'; url: string }): void {
    if (event.mode === 'cover') {
      this.projectForm.patchValue({ imageUrl: event.url });
      return;
    }
    const lines = this.imagebucketGalleryLines();
    if (lines.includes(event.url)) {
      return;
    }
    this.projectForm.patchValue({ imageUrlsText: [...lines, event.url].join('\n') });
  }
}
